import { useQuery } from '@tanstack/react-query';
import { mediaApi } from '@/api/media';
import { useAuthStore } from '@/store/authStore';

export type SignedMediaType = 'thumbnail' | 'download' | 'raw' | 'playlist.m3u8';

/**
 * Кешированный signed URL для одного media-файла.
 *
 * Фоновое поведение:
 *  - При первом use компонент отправляет POST /api/media/sign на 1 id.
 *  - React Query кеширует результат на staleTime=4 минуты (signed URL живёт
 *    5 мин, refresh за 1 мин до expire — гарантия что юзер не получит 401).
 *  - Cache shared между components — несколько `<MessageItem>` с одинаковым
 *    media_id отправят только 1 запрос, остальные подхватят кеш.
 *
 * Возвращает `null` пока запрос летит (caller должен показать placeholder/
 * skeleton). Возвращает строку URL когда готов.
 */
export function useSignedMediaUrl(
  mediaFileId: string | null | undefined,
  type: SignedMediaType,
): string | null {
  const accessToken = useAuthStore((s) => s.accessToken);
  const { data } = useQuery({
    queryKey: ['mediaSign', mediaFileId, type],
    queryFn: async () => {
      if (!mediaFileId) return null;
      const map = await mediaApi.signMedia([mediaFileId], [type]);
      return map[mediaFileId]?.[type] ?? null;
    },
    enabled: !!mediaFileId && !!accessToken,
    staleTime: 4 * 60 * 1000, // 4 мин
    gcTime: 5 * 60 * 1000, // 5 мин
    refetchOnWindowFocus: false,
  });
  return data ?? null;
}
